import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useInitiatePayment } from "../hooks/useInitiatePayment";
import { App, Card, Flex, Image, Modal, Space, Spin, Typography } from "antd";
import { Button } from "../custom";
import { useNavigate } from "react-router-dom";
import { useAtomValue } from "jotai";
import { paymentChannelAtom } from "../utils/store";

interface PaymentContextType {
  initiatePayment: (data: ProcessPaymentPayloadWithChannel) => void;
  isProcessing: boolean;
}

const PaymentContext = createContext<PaymentContextType | undefined>(undefined);

export const usePayment = () => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error("usePayment must be used within a PaymentProvider");
  }
  return context;
};

interface PaymentProviderProps {
  children: React.ReactNode;
}

interface RemitaInlineData {
  key: string;
  customerId: string;
  transactionId?: string;
  firstName: string;
  lastName: string;
  email: string;
  amount: number;
  narration: string;
  processRrr?: boolean;
  onSuccess: (response: unknown) => void;
  onError: (response: unknown) => void;
  onClose: () => void;
}

declare global {
  interface Window {
    RmPaymentEngine?: {
      init: (
        payload: RemitaInlineData & {
          extendedData?: {
            customFields?: Array<{ name: string; value: string }>;
            recurring?: Array<{
              endDate: number;
              frequency: string;
              maxUploadLimit: number;
              numberOfTimes: number;
              startDate: number;
            }>;
          };
        },
      ) => {
        openIframe: () => void;
        showPaymentWidget?: () => void;
      };
    };
  }
}

const remitaScriptCache: string[] = [];

export const PaymentProvider: React.FC<PaymentProviderProps> = ({
  children,
}) => {
  const navigate = useNavigate();
  const { notification } = App.useApp();
  const {
    makePayment,
    isLoading,
    paymentChannels,
    isLoadingChannels,
    refetchChannels,
  } = useInitiatePayment();
  const { Text } = Typography;
  const storedPaymentChannel = useAtomValue(paymentChannelAtom);

  const [channelDialogOpen, setChannelDialogOpen] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState<string>("");
  const [currentPaymentData, setCurrentPaymentData] =
    useState<ProcessPaymentPayloadWithChannel | null>(null);
  const [remitaPaymentData, setRemitaPaymentData] =
    useState<RemitaInlineData | null>(null);
  const [isRemitaScriptLoaded, setIsRemitaScriptLoaded] = useState(false);
  const [isLaunchingRemita, setIsLaunchingRemita] = useState(false);

  const shouldUseLiveRemita = useMemo(
    () =>
      Boolean(
        paymentChannels?.find(
          (channel) => channel.name?.trim().toLowerCase() === "remita",
        )?.paymentUrl &&
        !paymentChannels
          ?.find((channel) => channel.name?.trim().toLowerCase() === "remita")
          ?.paymentUrl.includes("demo.remita.net"),
      ),
    [paymentChannels],
  );

  useEffect(() => {
    if (!paymentChannels?.length) {
      return;
    }

    const normalizedStoredChannel = storedPaymentChannel?.trim().toLowerCase();
    const storedChannelMatch = paymentChannels.find(
      (channel) =>
        channel.name?.trim().toLowerCase() === normalizedStoredChannel,
    );

    setSelectedChannel((currentChannel) => {
      if (
        currentChannel &&
        paymentChannels.some(
          (channel) =>
            channel.name?.trim().toLowerCase() ===
            currentChannel.trim().toLowerCase(),
        )
      ) {
        return currentChannel;
      }

      return storedChannelMatch?.name ?? paymentChannels[0]?.name ?? "";
    });
  }, [paymentChannels, storedPaymentChannel]);

  useEffect(() => {
    if (!remitaPaymentData) {
      setIsRemitaScriptLoaded(false);
      return;
    }

    const scriptSrc = shouldUseLiveRemita
      ? "https://login.remita.net/payment/v1/remita-pay-inline.bundle.js"
      : "https://demo.remita.net/payment/v1/remita-pay-inline.bundle.js";

    if (window.RmPaymentEngine || remitaScriptCache.includes(scriptSrc)) {
      setIsRemitaScriptLoaded(true);
      return;
    }

    remitaScriptCache.push(scriptSrc);

    const script = document.createElement("script");
    script.src = scriptSrc;
    script.async = true;

    const handleScriptLoad = () => {
      setIsRemitaScriptLoaded(true);
    };

    const handleScriptError = () => {
      const scriptIndex = remitaScriptCache.indexOf(scriptSrc);
      if (scriptIndex >= 0) {
        remitaScriptCache.splice(scriptIndex, 1);
      }
      setIsRemitaScriptLoaded(false);
      notification.error({
        message: "Remita Error",
        description: "Unable to load Remita payment script.",
      });
    };

    script.addEventListener("load", handleScriptLoad);
    script.addEventListener("error", handleScriptError);
    document.body.appendChild(script);

    return () => {
      script.removeEventListener("load", handleScriptLoad);
      script.removeEventListener("error", handleScriptError);
    };
  }, [notification, remitaPaymentData, shouldUseLiveRemita]);

  const initiatePayment = async (data: ProcessPaymentPayloadWithChannel) => {
    setCurrentPaymentData(data);
    setChannelDialogOpen(true);

    if (!paymentChannels?.length) {
      await refetchChannels();
    }
  };

  const handleConfirmChannel = async () => {
    if (!currentPaymentData) return;
    try {
      const normalizedChannel = selectedChannel.trim().toLowerCase();
      const returnUrl = `${window.location.origin}/payments`;

      const result = await makePayment({
        ...currentPaymentData,
        channel: selectedChannel,
        returnUrl,
      });

      if (normalizedChannel === "remita") {
        const remitaData = result.redirectPayload?.Data as
          | Record<string, any>
          | undefined;
        const requestPayload = result.paymentResponse.requestPayLoad
          ? (JSON.parse(result.paymentResponse.requestPayLoad) as Record<
              string,
              any
            >)
          : undefined;
        const remitaChannelConfig = paymentChannels?.find(
          (channel) => channel.name?.trim().toLowerCase() === "remita",
        );
        const publicKey =
          remitaData?.publicKey ||
          remitaData?.apiKey ||
          remitaData?.key ||
          requestPayload?.IntegrationKey ||
          remitaChannelConfig?.integrationKey ||
          "";

        if (!publicKey) {
          notification.error({
            message: "Remita Error",
            description:
              "Remita key was not returned by the payment API or channel configuration.",
          });
          return;
        }

        const [firstName = "", ...lastNameParts] = (
          requestPayload?.CustomerName || ""
        )
          .trim()
          .split(" ");

        console.log(result);

        console.log(remitaPaymentData);

        setRemitaPaymentData({
          key: publicKey,
          customerId:
            result.paymentResponse.studentId ||
            result.paymentResponse.applicantId ||
            currentPaymentData.applicantId,
          transactionId:
            result.paymentResponse.paymentReference ||
            result.paymentResponse.merchantReference ||
            `${Date.now()}`,
          firstName,
          lastName: lastNameParts.join(" "),
          email: result.paymentResponse.customerEmail,
          amount: Number(
            remitaData?.amount || result.paymentResponse.amount || 0,
          ),

          narration:
            result.redirectPayload?.Description ||
            (currentPaymentData.paymentType === 2
              ? "Acceptance Fee Payment"
              : "Application Fee Payment"),
          onSuccess: (data: any) => {
            setRemitaPaymentData(null);
            navigate(`/payments?ref=${data.paymentReference}`);

            console.log(data);
          },
          onError: (response) => {
            notification.error({
              message: "Remita Error",
              description:
                (response as { message?: string })?.message ||
                "Remita payment could not be started.",
            });
            setRemitaPaymentData(null);
          },
          onClose: () => {
            setRemitaPaymentData(null);
          },
        });

        setChannelDialogOpen(false);
        setCurrentPaymentData(null);
        return;
      }

      setChannelDialogOpen(false);
      setCurrentPaymentData(null);
    } catch (_error) {
      // Errors are surfaced from the payment hook notification.
    }
  };

  const launchRemitaInline = () => {
    if (!remitaPaymentData || !window.RmPaymentEngine) {
      notification.error({
        message: "Remita Error",
        description: "Remita is not ready yet. Please try again.",
      });
      return;
    }

    try {
      setIsLaunchingRemita(true);
      console.log(remitaPaymentData);

      const handler = window.RmPaymentEngine.init({
        key: remitaPaymentData.key,
        customerId: remitaPaymentData.customerId,
        transactionId: remitaPaymentData.transactionId,
        firstName: remitaPaymentData.firstName,
        lastName: remitaPaymentData.lastName,
        email: remitaPaymentData.email,
        amount: remitaPaymentData.amount,
        narration: remitaPaymentData.narration,
        processRrr: true,
        extendedData: {
          customFields: [
            {
              name: "rrr",
              value: remitaPaymentData.transactionId!!,
            },
          ],
        },
        onSuccess: (response) => {
          setIsLaunchingRemita(false);
          remitaPaymentData.onSuccess(response);
          console.log(response);
        },
        onError: (response) => {
          setIsLaunchingRemita(false);
          remitaPaymentData.onError(response);
        },
        onClose: () => {
          setIsLaunchingRemita(false);
          remitaPaymentData.onClose();
        },
      });

      handler.openIframe();
    } catch (error: any) {
      setIsLaunchingRemita(false);
      notification.error({
        message: "Remita Error",
        description:
          error?.message || "Unable to open the Remita payment window.",
      });
    }
  };

  const contextValue: PaymentContextType = {
    initiatePayment,
    isProcessing: isLoading,
  };

  return (
    <PaymentContext.Provider value={contextValue}>
      {children}
      <Modal
        open={channelDialogOpen}
        centered
        title="Select Payment Channel"
        footer={null}
        onCancel={() => setChannelDialogOpen(false)}
      >
        {isLoadingChannels && !paymentChannels?.length ? (
          <Spin />
        ) : !paymentChannels?.length ? (
          <Typography.Text>
            No payment channels available at the moment.
          </Typography.Text>
        ) : (
          <div>
            {paymentChannels?.map((channel) => (
              <Card
                key={channel.name}
                hoverable
                onClick={() => setSelectedChannel(channel.name)}
                style={{
                  marginBottom: 12,
                  border: "2px solid",
                  borderColor:
                    selectedChannel === channel.name
                      ? "var(--color-primary)"
                      : "var(--color-border)",
                  borderRadius: 8,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
                onMouseEnter={(e) => {
                  if (selectedChannel !== channel.name) {
                    e.currentTarget.style.borderColor = "var(--color-primary)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedChannel !== channel.name) {
                    e.currentTarget.style.borderColor = "var(--color-border)";
                  }
                }}
                styles={{ body: { padding: 12, width: "100%" } }}
              >
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  {channel.imageUrl && (
                    <Image
                      src={channel.imageUrl}
                      alt={channel.name}
                      height={40}
                      preview={false}
                      style={{
                        width: "auto",
                        maxWidth: 150,
                        objectFit: "contain",
                      }}
                    />
                  )}
                  <Text style={{ fontWeight: 600, fontSize: "24px" }}>
                    {channel.name === "Cyperpay" ? "CyberPay" : channel.name}
                  </Text>
                </div>
              </Card>
            ))}
            <Flex justify="flex-end" gap={".75rem"}>
              <Button
                onClick={() => setChannelDialogOpen(false)}
                text="Cancel"
              />
              <Button
                text="Continue"
                onClick={handleConfirmChannel}
                isLoading={isLoading}
                disabled={!selectedChannel || isLoading}
              />
            </Flex>
          </div>
        )}
      </Modal>
      <Modal
        open={!!remitaPaymentData}
        centered
        title="Continue to Remita"
        footer={null}
        onCancel={() => setRemitaPaymentData(null)}
      >
        {remitaPaymentData && (
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Typography.Text>
              Your payment has been prepared. Click below to open the Remita
              payment widget.
            </Typography.Text>
            <Button
              text="Pay with Remita"
              onClick={launchRemitaInline}
              isLoading={isLaunchingRemita}
              disabled={!isRemitaScriptLoaded}
            />
          </Space>
        )}
      </Modal>
    </PaymentContext.Provider>
  );
};
